import React, { useState, useEffect, useRef } from 'react';
import { HiChevronDown, HiCheck, HiSearch } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { getAllPlatforms } from '../../platform/controller/platform-controller';


const CustomDropdown = ({ 
  name, 
  value, 
  onChange, 
  placeholder = "Select...", 
  error = false,
  icon: Icon,
  selectedPlatform
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedPlatformNames, setSelectedPlatformNames] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch platforms on mount
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoading(true);
        const data = await getAllPlatforms();
        const platformList = Array.isArray(data) ? data : [];
        setPlatforms(platformList);
      } catch (err) {
        console.error('Failed to fetch platforms:', err);
        setFetchError('Impossible de charger les plateformes');
      } finally {
        setLoading(false);
      }
    };
    fetchPlatforms();
  }, []);

  // Initialize selected ids/names from selectedPlatform prop (can be comma-separated)
  useEffect(() => {
    if (!selectedPlatform) {
      setSelectedIds([]);
      setSelectedPlatformNames([]);
      return;
    }

    const ids = selectedPlatform.toString().split(',').map(s => s.trim()).filter(Boolean);
    setSelectedIds(ids);

    if (platforms.length > 0) {
      const names = ids
        .map(id => platforms.find(p => p.id.toString() === id))
        .filter(Boolean)
        .map(p => p.name);
      setSelectedPlatformNames(names);
    }
  }, [selectedPlatform, platforms]);

  const filteredPlatforms = platforms.filter(platform =>
    platform.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectPlatform = (platform) => {
    const id = platform.id.toString();
    let newSelectedIds;
    if (selectedIds.includes(id)) {
      newSelectedIds = selectedIds.filter(i => i !== id);
    } else {
      newSelectedIds = [...selectedIds, id];
    }

    setSelectedIds(newSelectedIds);

    const names = newSelectedIds
      .map(i => platforms.find(p => p.id.toString() === i))
      .filter(Boolean)
      .map(p => p.name);
    setSelectedPlatformNames(names);

    // Emit change as comma-separated string or null
    const event = {
      target: {
        name: 'plateformId',
        value: newSelectedIds.length ? newSelectedIds.join(',') : null
      }
    };
    onChange(event);

    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setSearchTerm('');
  };

  const removeSelected = (id) => {
    const newSelected = selectedIds.filter(i => i !== id);
    setSelectedIds(newSelected);

    const names = newSelected
      .map(i => platforms.find(p => p.id.toString() === i))
      .filter(Boolean)
      .map(p => p.name);
    setSelectedPlatformNames(names);

    const event = {
      target: {
        name: 'plateformId',
        value: newSelected.length ? newSelected.join(',') : null
      }
    };
    onChange(event);
  };

  const clearAll = () => {
    setSelectedIds([]);
    setSelectedPlatformNames([]);
    const event = { target: { name: 'plateformId', value: null } };
    onChange(event);
  };

  return (
    <>
      <label htmlFor="platformId" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
        Plateforme
      </label>

      <div className="relative" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={handleToggle}
          className={`pl-10 pr-10 block w-full py-3 border ${
            error
              ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
          } rounded-md shadow-sm text-gray-900 sm:text-sm transition-all text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2`}
        >
          {/* Icon */}
          {Icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
          )}

          {/* Selected Value as tags */}
          <div className="w-full flex items-center flex-wrap gap-2">
            {selectedPlatformNames.length > 0 ? (
              <div className="flex flex-wrap gap-1 items-center">
                {selectedIds.map((id, idx) => (
                  <span key={id} className="inline-flex items-center bg-[#174540] text-white text-xs px-2 py-0.5 rounded-full">
                    <span className="max-w-[160px] truncate block pr-1">{selectedPlatformNames[idx] || id}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeSelected(id); }} className="ml-1 text-white opacity-80 hover:opacity-100 focus:outline-none">
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-400">Sélectionnez une plateforme</span>
            )}
          </div>

          {/* Dropdown Arrow */}
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <HiChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              }`} 
            />
          </div>
        </button>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden"
            >
              {/* Search Input - Fixed at top */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-3 flex items-center justify-between gap-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="pl-9 block w-full py-2 border border-gray-300 rounded-md focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                    placeholder={'Rechercher une platforme...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {selectedIds.length > 0 && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); clearAll(); }} className="text-sm text-[#174540] hover:text-[#0f2e2a] px-2 py-1 rounded-md border border-transparent hover:border-gray-200 bg-emerald-50">
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Options List - Scrollable */}
              <div className="max-h-48 overflow-y-auto">
                {filteredPlatforms.length > 0 ? (
                  filteredPlatforms.map((platform) => (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => handleSelectPlatform(platform)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors rounded-md my-1 flex items-center justify-between ${
                        selectedIds.includes(platform.id.toString())
                          ? 'bg-[#0f2e2a] text-white'
                          : 'text-gray-900 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="truncate">{platform.name}</span>
                      </div>
                      <div className="flex items-center">
                        {selectedIds.includes(platform.id.toString()) ? (
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white text-[#0f2e2a]">
                            <HiCheck className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-gray-200 text-gray-400">
                            {/* empty */}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-gray-500 text-sm">
                    Aucun résultat trouvé
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {fetchError && !error && <p className="mt-2 text-sm text-amber-600">{fetchError}</p>}
    </>
  );
};

export default CustomDropdown;